import { useContext, useEffect, useRef } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { MatomoTracker, TrackPageViewParams } from '@canvasflow/analytics';

import { TrackingContext } from './Tracking.context';
import { AppContext } from '../App.context';
import { TrackerLogger } from './Tracking.events';
import { MetaPixelTracker } from './trackers/MetaPixelTracker';
import { TiktokPixelTracker } from './trackers/TiktokPixelTracker';
import { SegmentTracker } from './trackers/SegmentTracker';
import { APP_USER_ID, ISSUE_LANG_KEY } from '@/constants';

const IS_INSTALLED_KEY = 'pwa-installed';

declare global {
	interface Window {
		dataLayer: any;
		gtag: any;
	}
}

export function useTrackingPageView({
	enabled,
	data,
	delay = 500,
}: TrackingPageViewProps) {
	const {
		gtag,
		gtm,
		locationChannel,
		matomo,
		plausible,
		metaPixel,
		tiktokPixel,
		segment,
	} = useContext(TrackingContext);
	// 🔥 Get real router location (source of truth)
	const routerState = useRouterState();
	const pathname = routerState.location.pathname;
	const userId = localStorage.getItem(APP_USER_ID) || undefined;

	// allow override if passed
	const location = pathname;

	// ✅ Track last successful event
	const lastTrackedRef = useRef<string | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!enabled || !location) return;

		// 🚫 prevent duplicate scheduling
		if (lastTrackedRef.current === location) return;

		// 🧹 clear previous timeout (important for fast navigation)
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			const title = document.title;

			// 🚫 double-check before firing (in case navigation changed again)
			if (lastTrackedRef.current === location) return;

			const previousPath = lastTrackedRef.current;

			lastTrackedRef.current = location;

			TrackerLogger.debug(`Page view "${location}"`, {
				title,
				location,
				previousPath,
				data,
			});

			const issueLang = localStorage.getItem(ISSUE_LANG_KEY);

			locationChannel.postMessage(location);

			if (gtag && window.gtag) {
				// ga.pageview(location, undefined, title);
				const gtagBody: GtagPageViewBoydy = {
					page_title: title,
					page_location: location,
				};

				if (userId) {
					gtagBody.user_id = `${userId}`;
				}

				if (issueLang) {
					gtagBody.language = issueLang;
				}
				window.gtag('event', 'page_view', gtagBody);
			}

			if (gtm && window.dataLayer) {
				const gtmEvent: GtmPageViewBody = {
					event: 'pageview',
					page: {
						url: location,
						title: title,
					},
				};

				if (issueLang) {
					gtmEvent.page.language = issueLang;
				}

				window.dataLayer.push(gtmEvent);
			}

			if (matomo) {
				TrackPageView.sendToMatomo(
					title,
					window.location.href,
					matomo,
					data,
				);
			}
			if (plausible) {
				const url = window.location.href;
				plausible.trackPageview(
					{
						url,
					},
					{
						callback: () =>
							console.log(
								`Plausible done sending page view: ${url}`,
							),
					},
				);
			}

			if (metaPixel) {
				MetaPixelTracker.pageView();
			}

			if (tiktokPixel) {
				TiktokPixelTracker.pageView();
			}

			if (segment) {
				// Tie the page view to a known user when we have one so
				// the Segment CDP can stitch the identity graph.
				if (userId) {
					SegmentTracker.identify(`${userId}`);
				}

				const segmentProperties: Record<string, any> = {
					title,
					path: location,
					url: window.location.href,
				};

				if (issueLang) {
					segmentProperties.language = issueLang;
				}

				SegmentTracker.pageView(segmentProperties);
			}
		}, delay);
		// 🧹 cleanup on unmount or route change
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
		// Intentionally scoped to [location, enabled, delay] only: this
		// effect debounces a fire-once-per-location page view. gtag/gtm/
		// matomo/etc. flip once from null to their tracker instance shortly
		// after mount and then stay stable, so they're read fresh enough via
		// closure. `data` is passed as a new object literal on every render
		// at several call sites (e.g. Issue.tsx) — adding it here would
		// reset the debounce timer on every parent re-render during data
		// loading and could indefinitely delay the tracked page view
		// instead of firing it once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, enabled, delay]);
}

interface GtagPageViewBoydy {
	page_title: string;
	user_id?: string;
	page_location: string;
	language?: string;
}

interface GtmPageViewBody {
	event: 'pageview';
	page: {
		url: string;
		title: string;
		language?: string;
	};
}

class TrackPageView {
	// Matomo Custom Dimensions Id
	static ISSUE_ID = 1;
	static ARTICLE_ID = 2;

	static sendToMatomo(
		title: string,
		location: string,
		matomo: MatomoTracker,
		data?: any,
	) {
		const pageView: TrackPageViewParams = {
			documentTitle: title,
			href: location,
		};

		if (data) {
			if (data?.language) {
				pageView.lang = data.language;
			}
			if (data?.issue?.IssueID) {
				pageView.issueId = `${data?.issue?.IssueID}`;
			}
			if (data?.article?.ArticleID) {
				pageView.articleId = `${data.article.ArticleID}`;
			}
		}

		console.debug(`Matomo page view: `, pageView);
		matomo.trackPageView(pageView);
	}
}

export function useTrackingInstallion() {
	const { sendEvent } = useContext(TrackingContext);
	const { isInstalled } = useContext(AppContext);
	useEffect(() => {
		if (localStorage.getItem(IS_INSTALLED_KEY)) {
			return;
		}

		if (!isInstalled || !sendEvent) {
			return;
		}

		localStorage.setItem(IS_INSTALLED_KEY, 'true');
		sendEvent({
			type: 'INSTALLED',
		});
	}, [isInstalled, sendEvent]);
}

interface TrackingPageViewProps {
	enabled: boolean;
	data?: {
		language?: string;
		issue?: any;
		article?: any;
	};
	delay?: number;
}
